import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { Chart } from "react-google-charts";
import { useTable, useFilters } from "react-table";
import "../styles/WeeklyReport.css"; // Tambahkan CSS untuk highlight

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://depotaircerdas.com/api";

const WeeklyReport = ({ username }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Ambil Token dari Local Storage
    const getToken = () => {
        const token = localStorage.getItem("token");
        return token ? `Bearer ${token}` : null;
    };

    // Fetch Data
    const fetchData = useCallback(async () => {
        const token = getToken();
        if (!token) {
            console.warn("🚨 Token tidak ditemukan. Redirect ke login.");
            window.location.href = "/login";
            return;
        }

        try {
            const res = await axios.get(`${API_BASE_URL}/reports/${username}/weekly`, {
                headers: { 
                    "Authorization": token,
                    "Content-Type": "application/json"
                }
            });
            setData(res.data);
        } catch (error) {
            console.error("❌ Error fetching weekly data:", error);
            setError("Gagal mengambil data mingguan.");
        } finally {
            setLoading(false);
        }
    }, [username]);

    // Fetch data saat komponen dimuat & auto-refresh tiap 1 Menit
    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 60000);
        return () => clearInterval(interval);
    }, [fetchData]);

    // **Proses Data**
    const groupedData = useMemo(() => {
        if (!data?.weekly) return [];

        return data.weekly.map(d => ({
            weekNumber: d.week,
            week: `Minggu ${d.week}`,
            volume: d.volume ? parseFloat(d.volume).toFixed(2) : "0.00",
            gallons: d.gallons || 0
        }));
    }, [data]);

    // **Sortir data agar:**
    // - **Di Chart:** Minggu 1 di kiri, Minggu terbaru di kanan.
    // - **Di Tabel:** Minggu terbaru di **bawah**, Minggu 1 di **atas**.
    const sortedChartData = useMemo(() => [...groupedData].sort((a, b) => a.weekNumber - b.weekNumber), [groupedData]); 
    const sortedTableData = useMemo(() => [...groupedData].sort((a, b) => a.weekNumber - b.weekNumber), [groupedData]); 

    // **Ambil Minggu terbaru**
    const latestWeekNumber = sortedChartData.length > 0 ? Math.max(...sortedChartData.map(d => d.weekNumber)) : null;

    // **Cek apakah data kosong**
    const isDataEmpty = sortedChartData.length === 0;

    // **Kolom Tabel**
    const columns = useMemo(() => [
        { Header: "Minggu", accessor: "week" },
        { Header: "Volume (L)", accessor: "volume" },
        { Header: "Total Galon", accessor: "gallons" }
    ], []);

    const tableInstance = useTable({ columns, data: sortedTableData }, useFilters);
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;

    return (
        <div className="report-container">
            <h3 className="report-title">📅 Laporan Mingguan - {username}</h3>
            <button className="refresh-btn" onClick={fetchData}>🔄 Refresh Data</button>

            {loading ? (
                <p className="loading">Memuat data...</p>
            ) : error ? (
                <p className="error-message">{error}</p>
            ) : isDataEmpty ? (
                <p className="error-message">⚠️ Data minggu ini kosong.</p>
            ) : (
                <>
                    {/* **Grafik Volume & Galon** */}
                    <Chart
                        chartType="LineChart"
                        width="100%"
                        height="350px"
                        data={
                            sortedChartData.length > 0 
                            ? [["Minggu", "Volume (L)", "Galon"], ...sortedChartData.map(d => [d.week, Number(d.volume), d.gallons])]
                            : [["Minggu", "Volume (L)", "Galon"], ["-", 0, 0]] // Mencegah error jika data kosong
                        }
                        options={{
                            title: "Trend Penggunaan Air (Mingguan)",
                            curveType: "function",
                            legend: { position: "top" },
                            colors: ["#007bff", "#dc3545"],
                            series: {
                                0: { targetAxisIndex: 0 },
                                1: { targetAxisIndex: 1 }
                            },
                            vAxes: {
                                0: { title: "Volume (L)", textStyle: { color: "#007bff" } },
                                1: { title: "Galon", textStyle: { color: "#dc3545" } }
                            }
                        }}
                    />

                    {/* **Tabel Data** */}
                    <table {...getTableProps()} className="dashboard-table">
                        <thead>
                            {headerGroups.map(headerGroup => (
                                <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                                    {headerGroup.headers.map(column => (
                                        <th {...column.getHeaderProps()} key={column.id}>{column.render("Header")}</th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                            {rows.map(row => {
                                prepareRow(row);
                                return (
                                    <tr {...row.getRowProps()} key={row.id}
                                        className={row.original.weekNumber === latestWeekNumber ? "latest-week" : ""}>
                                        {row.cells.map(cell => (
                                            <td {...cell.getCellProps()} key={cell.column.id}>{cell.render("Cell")}</td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </>
            )}
        </div>
    );
};

export default WeeklyReport;
